/**
 * Closed-project lock predicate (spec §9, patch E2).
 *
 *   "Setting Project Closed locks all fields against Assistant edits;
 *    Admin retains edit access."
 *
 * Returns true when the UI should disable an edit affordance for the
 * current user — i.e. when the project is closed AND the user is not
 * an Admin.
 *
 * Use this at every edit gate that previously used a bare
 * `projectClosedAt !== null` / `lifecycleState === 'CLOSED'` check. The
 * server still enforces the same rule independently — this is a UX
 * convenience that hides affordances the user wouldn't be able to use.
 *
 * @param project  Anything with a `projectClosedAt` or `lifecycleState`
 *                 field. Accepts either shape so callers can pass the
 *                 raw `Project` row or a derived view.
 * @param isAdmin  Whether the current user has the ADMIN role.
 */
export function isLockedForUser(
  project:
    | { projectClosedAt: string | null }
    | { lifecycleState: string }
    | { projectClosedAt: string | null; lifecycleState?: string },
  isAdmin: boolean,
): boolean {
  if (isAdmin) return false; // Admin retains edit access; never locked.

  // Prefer the lifecycleState signal when present (already-derived), fall
  // back to projectClosedAt. Both are authoritative; either one being
  // truthy means closed.
  if ('lifecycleState' in project && project.lifecycleState === 'CLOSED') return true;
  if ('projectClosedAt' in project && project.projectClosedAt !== null) return true;
  return false;
}
